import React, { useEffect, useMemo, useState } from 'react';

import { CircularProgress, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import AdditionalFiles from '../../components/caseActions/AdditionalFiles';
import CaseActions from '../../components/caseActions/CaseActions';
import CaseDetails from '../../components/caseDetails/CaseDetails';
import CaseInfo from '../../components/caseInfo/CaseInfo';
import { ACTIONS } from '../../constants/logs';
import { caseStatuses } from '../../models/Case';
import { WorkspaceProductType } from '../../models/Workspace';
import LogService from '../../services/log.service';
import useBlocRef from '../../utils/useBlocRef.hook';
import PatientReport from '../patientReport/PatientReport';

const useStyles = makeStyles(() => {
  return {
    detailsSection: {
      borderLeft: '1px solid #E7E7E7',
      minHeight: '80vh',
    },
    pageContainer: {
      padding: '2rem 3rem',
    },
    patientName: {
      color: '#172B4D',
      fontSize: '24px',
      fontWeight: 'bold',
      marginRight: '10px',
    },
    sectionContainer: {
      paddingLeft: '2rem',
    },
    studyName: {
      color: '#999999',
      fontSize: '24px',
      marginRight: '10px',
    },
    viewer: {
      maxWidth: '95vw',
      width: '95vw',
    },
  };
});

export default () => {
  const classes = useStyles();
  const history = useHistory();
  const blocRef = useBlocRef('caseDetailsBloc');
  const [auth, setAuth] = useState(blocRef.current.auth$.getValue());
  const [selectedCase, setSelectedCase] = useState(blocRef.current.selectedCase$.getValue());
  const [caseStatus, setCaseStatus] = useState(blocRef.current.caseStatus$.getValue());
  const [childResources, setChildResources] = useState([]);
  const [columns, setColumns] = useState(['15%', '60%', '25%']);
  const [onlinePortalAccess, setOnlinePortalAccess] = useState(false);
  const [uploadSharedInfo, setUploadSharedInfo] = useState({});
  const [fileStatus, setFileStatus] = useState({
    existingStudy: false,
    files: [],
    hasReport: false,
    imageCount: 0,
    reportUploaded: false,
    study: null,
    uploaded: false,
    uploadedReport: [],
  });
  const [loadingCase, setLoadingCase] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldReturn, setShouldReturn] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [loadingSharedImageDetail, setLoadingSharedImageDetail] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [workspace, setWorkspace] = useState({});
  const [isLogged, setIsLogged] = useState(false);
  const { search } = useLocation();
  const { caseId } = queryString.parse(search);

  if (!selectedCase && !caseId) {
    history.push('/case-list');
  }

  useEffect(() => {
    const authSub = blocRef.current.auth$.subscribe(setAuth);
    const caseSub = blocRef.current.selectedCase$.subscribe(setSelectedCase);
    const workspaceSub = blocRef.current.userWorkspace$.subscribe(setWorkspace);
    const fileStatusSub = blocRef.current.fileStatus$.subscribe(setFileStatus);
    const caseStatusSub = blocRef.current.caseStatus$.subscribe((status) => {
      setCaseStatus(status);
      setColumns(status.reportSubmitted ? ['15%', '40%', '45%'] : ['15%', '60%', '25%']);
    });
    const caseUpdateSub = blocRef.current.listenToCaseUpdate().subscribe();
    const additionalFilesUpdateSub = blocRef.current.listenToAdditionalFilesUpdate().subscribe();
    const loadingCaseSub = blocRef.current.loadingCase$.subscribe(setLoadingCase);
    const isSubmittingSub = blocRef.current.isSubmitting$.subscribe(setIsSubmitting);
    const shouldReturnSub = blocRef.current.shouldReturn$.subscribe(setShouldReturn);
    const isArchivingSub = blocRef.current.isArchiving$.subscribe(setIsArchiving);
    const loadingSharedImageDetailSub = blocRef.current.loadingSharedImageDetail$.subscribe(setLoadingSharedImageDetail);
    const userDetailsSub = blocRef.current.userDetails$.subscribe(setUserDetails);
    const onlinePortalAccessSub = blocRef.current.onlinePortalAccess$.subscribe(setOnlinePortalAccess);
    const uploadSharedInfoSub = blocRef.current.uploadSharedInfo$.subscribe(setUploadSharedInfo);
    const childResourcesSub = blocRef.current.childResources$.subscribe(setChildResources);

    blocRef.current.getCaseById(caseId).then();

    return () => {
      authSub.unsubscribe();
      caseSub.unsubscribe();
      workspaceSub.unsubscribe();
      fileStatusSub.unsubscribe();
      caseStatusSub.unsubscribe();
      caseUpdateSub.unsubscribe();
      additionalFilesUpdateSub.unsubscribe();
      loadingCaseSub.unsubscribe();
      isSubmittingSub.unsubscribe();
      shouldReturnSub.unsubscribe();
      isArchivingSub.unsubscribe();
      loadingSharedImageDetailSub.unsubscribe();
      userDetailsSub.unsubscribe();
      onlinePortalAccessSub.unsubscribe();
      uploadSharedInfoSub.unsubscribe();
      childResourcesSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCase || isLogged) return;

    new LogService().create({
      action: ACTIONS.CASE_VIEWED,
      details: {
        'Case Id': selectedCase.id,
        'Case Name': selectedCase.description,
        'Case Status': selectedCase.status.label,
        'Case Workspace': selectedCase.originalWorkspace?.name,
      },
    });

    setIsLogged(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCase]);

  useEffect(() => {
    if (shouldReturn) history.push('/cases');
  }, [history, shouldReturn]);

  const handleCaseAction = (action) => {
    switch (action) {
      case 'claim':
        blocRef.current.claimCase(selectedCase.id);
        break;
      case 'record':
        blocRef.current.launchRecorder(selectedCase.id);
        break;
      case 'aiRecordModal':
        blocRef.current.openLaunchAiRecordingDialog(selectedCase.id);
        break;
      case 'aiRecord':
        blocRef.current.launchRecorder(selectedCase.id, true);

        new LogService().create({
          action: ACTIONS.RECORDER_LAUNCHED,
          details: {
            'Ai mode': true,
          },
        });

        break;
      case 'optout':
        blocRef.current.rejectCase(selectedCase.id);
        break;
      case 'cancelReview':
        blocRef.current.cancelReview(selectedCase.id);
        break;
      case 'discard':
        blocRef.current.discardReview(selectedCase.id);
        break;
      case 'editVideo':
        blocRef.current.launchRecorder(selectedCase.id);
        break;
      case 'startReview':
        blocRef.current.startReview(selectedCase.id);
        break;
      case 'sendReview':
        blocRef.current.sendForReview(selectedCase.id);
        break;
      case 'signoff':
        blocRef.current.signOff(selectedCase.id);
        break;
      case 'complete':
        blocRef.current.caseToComplete$.next(selectedCase);
        history.push('/add-case?mode=complete');
        break;
      case 'submitClinicUpload':
        blocRef.current.openDialogSubmitClinicUploadFiles();
        break;
      case 'rejectPendingUpload':
        blocRef.current.openDialogRejectPendingUpload(selectedCase.id);
        break;
      case 'archive':
        blocRef.current.archiveCase(selectedCase.id);
        break;
      case 'unarchive':
        blocRef.current.unarchiveCase(selectedCase.id);
        break;
      case 'bypassImagesAndReport':
        blocRef.current.bypassImagesAndReport(selectedCase.id);
        break;
      default:
        break;
    }
  };

  const handleOpenReport = (link) => blocRef.current.openPdfReport(link);
  const handleOpenReportViewer = (reports) => blocRef.current.openReportViewer(reports, { paper: classes.viewer });
  const handleViewMaterials = (material) => blocRef.current.viewMaterial(material);
  const handleRemoveReport = () => blocRef.current.removeReport();

  const handleOpenChangeTitle = () => blocRef.current.openChangeTitle();
  const handleRemoveFiles = () => blocRef.current.removeFiles();
  const handleReportUpload = (files) => blocRef.current.uploadReports(files);
  const handleFilesUpload = (files) => blocRef.current.onFilesUpload(files);

  const handleSelectSharedImage = () => blocRef.current.selectSharedImage();
  const handleRemoveSharedImage = () => blocRef.current.removeSharedImage();
  const handleViewSharedImage = (material) => blocRef.current.openViewer(material);

  const handleViewAdditionalFiles = () => blocRef.current.openAdditionalFilesDialog();
  const handleViewAdditionalFile = (file) => blocRef.current.manageAdditionalFilesViewer(file);
  const handleGiveOnlinePortalAccess = () => blocRef.current.manageOnlinePortalAccess(true);
  const handleUpdateUploadSharedInfo = (value, target, isValid) => blocRef.current.updateUploadSharedInfo(value, target, isValid);
  const handleOpenImageSelectionDialog = () => blocRef.current.openImageSelectionDialog();
  const handleOpenRadiologyReportDialog = () => blocRef.current.openRadiologyReportDialog();

  const shouldHideActionsPanel = useMemo(() => {
    const isMediphanyWorkspace = userDetails?.workspace?.isMediphanyWorkspace || false;
    const isCreatorMode = userDetails?.workspace?.creatorMode || false;
    const isAssignedToUser = userDetails?.id && userDetails?.id === caseStatus?.data?.assigneeId;
    const isLawFirmWorkspace = workspace?.productType === WorkspaceProductType.LawFirm.value;

    const allowedCases = ['pending-upload'];
    let isCurrentCaseAllowed = allowedCases.includes(caseStatus.status) && !isLawFirmWorkspace;

    if (caseStatus.status === 'in-progress' && selectedCase?.videoReport) {
      isCurrentCaseAllowed = true;
    }

    if (['in-progress', 'in-review', 'review'].includes(caseStatus.status) && selectedCase?.videoReport && isLawFirmWorkspace) {
      isCurrentCaseAllowed = true;
    }

    return !(isMediphanyWorkspace || isCreatorMode || isAssignedToUser) && !isCurrentCaseAllowed;
  }, [caseStatus, userDetails, selectedCase, workspace?.productType]);

  const shouldAdditionalFileButton = useMemo(() => {
    const allowedCasesStatuses = ['pending-upload', 'upload-shared', 'pending-images', 'new', 'delayed', 'in-progress', 'review', 'in-review', 'completed'];
    const isCurrentCaseAllowed = allowedCasesStatuses.includes(caseStatus.status);

    return userDetails.role === 'super-admin' && isCurrentCaseAllowed;
  }, [caseStatus, userDetails]);

  const workspaceMainFileType = useMemo(() => {
    return workspace?.mainFileType || 'dicom';
  }, [workspace]);

  const mainColumnWidth = shouldHideActionsPanel ? '85%' : columns[1];

  if (loadingCase) {
    return (
      <Container maxWidth="xl" className={classes.pageContainer}>
        <CircularProgress />
      </Container>
    );
  }

  if (selectedCase?.status?.value === caseStatuses.completed.value && selectedCase?.dicomStudy?.id) {
    return <PatientReport />;
  }

  return (
    <>
      {selectedCase && (
        <Container maxWidth="xl" className={classes.pageContainer}>
          <Grid container item justify="flex-start">
            <Grid container item className={classes.sectionContainer} xs={12} style={{ marginBottom: '1rem' }}>
              <Typography className={classes.patientName}> {selectedCase?.patient?.name} </Typography>
              <Typography className={classes.studyName}> {selectedCase?.description} </Typography>
            </Grid>
            <Grid item className={classes.sectionContainer} style={{ flexBasis: columns[0], maxWidth: columns[0] }}>
              <CaseInfo onOpenReport={handleOpenReport} onOpenReportViewer={handleOpenReportViewer} caseStudy={selectedCase} />
            </Grid>

            <Grid item className={`${classes.sectionContainer} ${classes.detailsSection}`} style={{ flexBasis: mainColumnWidth, maxWidth: mainColumnWidth }}>
              <CaseDetails
                onViewAdditionalFile={handleViewAdditionalFile}
                onViewAdditionalFiles={handleViewAdditionalFiles}
                childResources={childResources}
                auth={auth}
                caseStudy={selectedCase}
                reportSubmitted={caseStatus.reportSubmitted}
                onlinePortalAccess={onlinePortalAccess}
                onUpdateUploadSharedInfo={handleUpdateUploadSharedInfo}
                uploadSharedInfo={uploadSharedInfo}
              />
            </Grid>

            {!shouldHideActionsPanel ? (
              <Grid item className={classes.sectionContainer} style={{ flexBasis: columns[2], maxWidth: columns[2] }}>
                <CaseActions
                  auth={auth}
                  fileStatus={fileStatus}
                  onChangeTitle={handleOpenChangeTitle}
                  onRemoveFiles={handleRemoveFiles}
                  onReportUpload={handleReportUpload}
                  onFilesUpload={handleFilesUpload}
                  onRemoveReport={handleRemoveReport}
                  onSelectSharedImage={handleSelectSharedImage}
                  onRemoveSharedImage={handleRemoveSharedImage}
                  onOpenRadiologyReportDialog={handleOpenRadiologyReportDialog}
                  onViewSharedImage={handleViewSharedImage}
                  hideFileControls={false}
                  onViewMaterial={handleViewMaterials}
                  caseStatus={caseStatus}
                  onCaseAction={handleCaseAction}
                  loadingSharedImageDetail={loadingSharedImageDetail}
                  isSubmitting$={blocRef.current.isSubmitting$}
                  isSubmitting={isSubmitting}
                  isArchiving$={blocRef.current.isArchiving$}
                  isArchiving={isArchiving}
                  isBypassing$={blocRef.current.isBypassing$}
                  workspace={userDetails?.workspace}
                  onGiveOnlinePortalAccess={handleGiveOnlinePortalAccess}
                  uploadSharedInfo={uploadSharedInfo}
                  caseStudy={selectedCase}
                  workspaceMainFileType={workspaceMainFileType}
                  onOpenImageSelectionDialog={handleOpenImageSelectionDialog}
                  radiologyReportRequired={workspace?.radiologyReportRequired}
                />

                {shouldAdditionalFileButton ? <AdditionalFiles caseData={selectedCase} /> : null}
              </Grid>
            ) : null}
          </Grid>
        </Container>
      )}
    </>
  );
};
